/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRequestCommunitiesAdminFlaggable } from "@packages/sdk";

import type { ThemeType } from "../../../../../global/emotion";
import { colors, radius, spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Skeleton } from "../Skeleton/Skeleton.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const baseStyle: Interpolation = {
  borderRadius: radius.full,
};
const counterStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.accentMedium,
  color: colors.white,
  display: "flex",
  justifyContent: "center",
});

const stylesSize: Record<
  NonNullable<ReportedCounterProps["size"]>,
  Interpolation
> = {
  m: {
    height: spacing.l,
    width: spacing.l,
  },
  l: {
    height: 40,
    width: 40,
  },
};

export type ReportedCounterProps = WithAsChild<
  Omit<HallowElementProps<typeof hallowElement>, "children">
> & {
  communityId: number;
  loading?: boolean;
  size?: "m" | "l";
};

export const ReportedCounter = ({
  asChild = false,
  communityId,
  loading,
  size = "m",
  ...props
}: ReportedCounterProps) => {
  const DeterminedElement = determineElementFromAsChild({
    asChild,
    hallowElement,
  });

  const { query: communitiesAdminFlaggableQuery } =
    useRequestCommunitiesAdminFlaggable({
      id: communityId,
    });

  if (loading || !communitiesAdminFlaggableQuery.data)
    return <Skeleton css={[baseStyle, stylesSize[size]]} />;

  if (communitiesAdminFlaggableQuery.data?.total === 0) return null;

  return (
    <DeterminedElement
      css={[baseStyle, counterStyle, stylesSize[size]]}
      {...props}
    >
      <Text size={size === "m" ? "xs" : "l"} type="title">
        {communitiesAdminFlaggableQuery.data?.total}
      </Text>
    </DeterminedElement>
  );
};

ReportedCounter.displayName = "ReportedCounter";
